import { capitalize, compact, orderBy } from 'lodash-es';
import { differenceInMinutes } from 'date-fns';
import { SortDescriptor } from '@ds';
import {
  CandidateMatchType,
  LastCertificationAtHubInfo,
  LastCertificationsAtHubInfo,
  OcsConsoleData,
  ResourceStatus,
} from '@/api/apis/resources';
import { CaseOCSConsoleResource, CaseResource, CaseResourceCombined, OrganType } from '@/api/apis/case';
import { LastCertificationAtHubStatusType } from '@/api/types';

export const mapSelected = <R, K extends keyof R>(selectedIds: R[K] | R[K][], entities: R[], keyField: K) => {
  return entities.map((e) => {
    let isSelected;
    if (Array.isArray(selectedIds)) {
      isSelected = selectedIds.includes(e[keyField]);
    } else {
      isSelected = selectedIds === e[keyField];
    }
    return isSelected ? { ...e, isSelected } : e;
  });
};

export const getOCSDisabledKeys = (tableRows: OcsConsoleData[]) =>
  tableRows
    .filter((ocs) => !ocs.reservations.every((r) => [ResourceStatus.Done, ResourceStatus.Canceled].includes(r.status)))
    .map((ocs) => ocs.id);

export const sortCandidates = <T>(
  candidates: T[],
  sortDescriptor?: SortDescriptor,
  sortingProperty: string = 'employeeFullName',
) => {
  if (sortDescriptor)
    return orderBy(candidates, sortingProperty, sortDescriptor.direction === 'ascending' ? 'asc' : 'desc');
  return candidates;
};

export const getDevicesListWithAssigned = (list: OcsConsoleData[], assignedDevices: CaseResourceCombined[] = []) => {
  if (!assignedDevices.length || !list.length) return list;

  const hubId = list[0].nopHub;
  const devicesToAdd = assignedDevices.filter(
    (resource) =>
      !list.find((device) => device.id === resource.resourceId) &&
      (resource as CaseResource<CaseOCSConsoleResource>)?.data?.nopHub === hubId,
  );

  return [...devicesToAdd.map((r) => r.data).filter((d): d is OcsConsoleData => !!d), ...list];
};

const hoursUntilCeil = (iso?: string): number | undefined => {
  if (!iso) return undefined;
  const now = new Date();
  const mins = differenceInMinutes(new Date(iso), now);
  if (mins < 0) return 0;
  return Math.ceil(mins / 60);
};

const toHoursText = (n?: number) => (typeof n === 'number' ? `${n} hour(s)` : 'soon');

type LastCertParams = {
  lastCertificationAtHubInfo?: LastCertificationsAtHubInfo;
  matchType?: CandidateMatchType;
  roleLabel?: string;
  csName?: string;
  hoursWindow?: number;
  withMayBecomeLast?: boolean;
};

export const getLastCertificationReservationConditions = ({
  lastCertificationAtHubInfo,
  matchType,
  roleLabel = 'CS',
  csName,
  withMayBecomeLast,
}: LastCertParams): string[] => {
  const getMessage = (certKeyName: string, lastCertData: LastCertificationAtHubInfo | null) => {
    const organName = capitalize(certKeyName) as OrganType;
    const hoursText = lastCertData?.statusChangeAt ? toHoursText(hoursUntilCeil(lastCertData.statusChangeAt)) : '';
    const prefixString = compact([roleLabel, csName]).join(' ');
    const isMediumMatch = matchType === CandidateMatchType.Medium;
    const isMissMatch = matchType === CandidateMatchType.Miss;

    switch (lastCertData?.lastCertificationAtHubStatus) {
      case LastCertificationAtHubStatusType.Last: {
        if (matchType && !isMediumMatch) return null;
        return `${prefixString} is the last available with ${organName} certification in this Hub.`;
      }
      case LastCertificationAtHubStatusType.LastWithNextSoon: {
        if (matchType && !isMediumMatch) return null;
        return `${prefixString} is the last available with ${organName} certification in this Hub. Another CS will be available in ${hoursText}.`;
      }
      case LastCertificationAtHubStatusType.NotLastButBecomeLast: {
        if (!withMayBecomeLast || (matchType && isMissMatch)) return null;
        return `${prefixString} is not currently the last available with ${organName} certification in this Hub, but may become the last in ${hoursText}.`;
      }
      case LastCertificationAtHubStatusType.NotLast:
      default:
        return null;
    }
  };

  return lastCertificationAtHubInfo
    ? compact(Object.entries(lastCertificationAtHubInfo).map(([certKey, info]) => getMessage(certKey, info)))
    : [];
};
